<template lang="pug">
ja-content(:pageTitle='pageTitle', :pageActions='pageActions')
  .ContentPreviewPage
    // Preview AMP
    .ContentPreviewPage-Preview
      h2 AMP Preview
      ce-content-preview(
        v-if='content?.config',
        :config='content.config',
        :on-compile-content='(value) => onCompileContent({ format: "amp", value })',
        :projectId='projectId',
        format='amp',
        class=''
      )

    // Preview HTML
    .ContentPreviewPage-Preview
      h2 HTML Preview
      ce-content-preview(
        v-if='content?.config',
        :config='content.config',
        :on-compile-content='(value) => onCompileContent({ format: "html", value })',
        :projectId='projectId',
        format='html',
        class=''
      )

  // Modal
  b-modal(centered, ref='modal-send-email', v-model='modalShow', size='lg')
    // Modal Title
    template(v-slot:modal-title)
      i.mdi.mdi-email-send
      span {{ $t("send_email") }}

    .form-spinner(v-if='showSendEmailSpinner' class='d-flex justify-content-center')
      b-spinner(label="Sending...")

    // Modal Content
    form.form(ref='formSendEmail' v-show='showSendEmailForm')
      ja-form-field#senderName(
        :label='$t("sender_name")',
        :on-change='handleChangeEmailSettings',
        :placeholder='$t("sender_name_placeholder")',
        name='senderName',
        required
        type='text'
      )
      ja-form-field#senderEmail(
        :label='$t("sender_email")',
        :on-change='handleChangeEmailSettings',
        :placeholder='$t("sender_placeholder")',
        name='senderEmail',
        required
        type='email'
      )
      ja-form-field#recipientEmail(
        :label='$t("to")',
        :on-change='handleChangeEmailSettings',
        :placeholder='$t("recipient_placeholder")',
        name='recipientEmail',
        required
        type='email'
      )
      ja-form-field#subject(
        :label='$t("subject")',
        :on-change='handleChangeEmailSettings',
        :placeholder='$t("subject_placeholder")',
        name='subject',
        required
        type='text'
      )

    // Modal Footer
    template(v-slot:modal-footer)
      ja-button(
        :on-click='createExpressCampaign',
        :text='$t("create_express_campaign")',
        icon='ti-plus',
        type='outline-secondary'
      )
      ja-button(
        :on-click='sendEmail',
        :text='$t("send_test")',
        icon='ti-control-play',
        type='primary'
      )
</template>

<script>
import common from '@apps/Common/common.store'
import { setTemplateClone } from '@apps/Dymal/Templates/Templates/store/templates.store'

import {
  addAudienceToCampaign,
  createCampaign,
  createCampaignContent
} from '@apps/Dymal/Campaigns/Campaigns/store/campaigns.store'

import { createAudience } from '@apps/Dymal/Audiences/Audiences/store/audiences.store'
import { createContact } from '@apps/Dymal/Audiences/Contacts/store/contacts.store'
import { TemplatesService } from '@apps/Dymal/Templates/Templates/service/templates.service'

export default {
  name: 'ContentPreviewPage',

  props: {
    content: {
      type: Object,
      default: () => ({})
    },
    description: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    isCompleted: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      showSendEmailForm: true,
      showSendEmailSpinner: false,
      modalShow: false,
      emailSettings: {},
      compiledContent: {
        amp: '',
        html: '',
      },
    }
  },

  computed: {
    pageActions () {
      if (this.isCompleted) {
        return [
          {
            callback: this.useAsTemplate,
            icon: 'ti-layers',
            text: 'use_as_template',
          }
        ]
      }

      return [
        {
          callback: this.toggleModalSendEmail,
          icon: 'ti-email',
          text: 'send_test',
        }
      ]
    },
    campaignData () {
      const { recipientEmail, senderEmail, senderName, subject } =
        this.emailSettings

      return {
        name: `Express Campaign for ${recipientEmail}`,
        description: '',
        from: senderEmail,
        from_name: senderName,
        subject,
      }
    },

    pageTitle () {
      return `${this.$t('preview')}: ${this.title}`
    },

    projectId: () => common.user.project_id,
  },

  methods: {
    async createExpressCampaign () {
      if (!this.isEmailFormValid()) {
        return
      }

      this.toogleEmailFormSpinner()

      // Create Campaign
      const newCampaign = await createCampaign(this.campaignData)

      // Create Audience and Contact
      const { recipientEmail } = this.emailSettings

      const newAudience = await createAudience({ name: `Express Audience for ${recipientEmail}` })

      await createContact({
        audience_id: newAudience.id,
        email: recipientEmail,
      })

      await addAudienceToCampaign(newCampaign.id, newAudience.id)

      // Create Campaign Content
      await createCampaignContent({
        campaign_id: newCampaign.id,
        config: JSON.stringify(this.content.config),
        content_html: this.compiledContent.html,
        content_amp: this.compiledContent.amp,
        preview: this.content.preview,
      })

      this.$router.push({
        name: 'campaign',
        params: { id_campaign: newCampaign.id }
      })
    },

    handleChangeEmailSettings (value, name) {
      this.$set(this, 'emailSettings', { ...this.emailSettings, [name]: value })
    },

    onCompileContent ({ format, value }) {
      this.compiledContent[format] = value
    },

    isEmailFormValid () {
      const formEl = this.$refs.formSendEmail
      const isValid = formEl.reportValidity()
      return isValid
    },

    async sendEmail () {
      if (!this.isEmailFormValid()) {
        return
      }

      this.toogleEmailFormSpinner()

      const { recipientEmail, senderEmail, senderName, subject } =
        this.emailSettings

      const payload = {
        from: senderEmail,
        from_name: senderName,
        subject,
        contacts: [
          {
            email: recipientEmail,
          },
        ],
        content: {
          ...this.compiledContent,
          text: this.description,
        },
      }

      await TemplatesService.sendEmail(payload)

      this.toggleModalSendEmail()
    },

    toogleEmailFormSpinner () {
      this.showSendEmailForm = false
      this.showSendEmailSpinner = true
    },

    toggleModalSendEmail () {
      this.showSendEmailForm = true
      this.showSendEmailSpinner = false
      this.$refs['modal-send-email'].toggle()
    },

    async useAsTemplate () {
      const template = {
        name: this.title,
        description: this.description,
        preview: this.content.preview,
        config: this.content.config,
        content_html: this.content.content_html,
        content_amp: this.content.content_amp,
      }

      setTemplateClone(template)

      this.$router.push({ name: 'template-new' })
    },
  },
}
</script>

<style lang="sass" scoped>
.ContentPreviewPage
  display: grid
  height: calc(100vh - 300px)
  justify-items: center
  grid-template-columns: 1fr 1fr
  gap: 60px

  &-Preview
    width: 100%
</style>
